<template>
  <div
    :id="`recipe_${recipe.id}`"
    class="flex flex-col gap-0 hover:opacity-90 transition-opacity rounded-[32px]"
    style="box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.08)"
  >
    <Link
      :href="recipe.path"
      class="flex flex-col gap-0"
      style="text-decoration: none"
    >
      <!-- Image -->
      <div
        class="relative overflow-hidden rounded-t-[32px]"
        style="aspect-ratio: 1 / 1"
      >
        <img
          class="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
          :src="recipe.cover_image?.medium"
          :alt="recipe.title"
          loading="lazy"
        />
        <!-- Draft / hidden ribbon -->
        <div
          v-if="recipe.state === 'draft' || recipe.state === 'hidden'"
          class="absolute right-0 top-0 h-16 w-16"
        >
          <span
            :class="
              recipe.state === 'draft' ? 'bg-emerald-500' : 'bg-slate-400'
            "
            class="py-1 absolute transform rotate-45 text-white text-center right-[-50px] top-[15px] w-[170px] text-xs"
            >{{
              recipe.state.charAt(0).toUpperCase() + recipe.state.slice(1)
            }}</span
          >
        </div>
      </div>

      <!-- Text -->
      <div class="pb-6 bg-white rounded-b-[32px] px-6 pt-3">
        <span
          v-if="recipe.category"
          class="font-[Poppins] font-bold text-[10px] tracking-[1px] uppercase"
          :style="{ color: categoryColor(recipe.category) }"
          >{{ recipe.category }}</span
        >
        <h3
          class="font-[Poppins] font-bold text-[15px] text-[#191c1e] leading-[21px] mt-1 mb-[6px]"
        >
          {{ recipe.title }}
        </h3>
        <hr class="border-t border-[#eceef0] my-2" />

        <!-- Meta row -->
        <div class="flex items-center gap-2">
          <span class="text-[12px] text-[#6c7a77] whitespace-nowrap">
            ⏱ {{ (recipe.prep_time || 0) + (recipe.cook_time || 0) }} min
          </span>
          <span v-if="recipe.difficulty" class="text-[#d0d5d4] text-[10px]"
            >•</span
          >
          <span
            v-if="recipe.difficulty"
            class="flex items-center gap-1 whitespace-nowrap"
          >
            <span
              class="inline-block w-[6px] h-[6px] rounded-full shrink-0"
              :style="{ backgroundColor: difficultyColor(recipe.difficulty) }"
            />
            <span
              class="text-[12px] font-semibold"
              :style="{ color: difficultyColor(recipe.difficulty) }"
              >{{
                recipe.difficulty.charAt(0).toUpperCase() +
                recipe.difficulty.slice(1)
              }}</span
            >
          </span>
          <div class="ml-auto">
            <template v-if="currentUser">
              <Button
                v-if="isLiked"
                variant="link"
                size="icon"
                class="cursor-pointer h-auto p-0"
                @click.prevent="
                  unlikeForm.delete(recipe.unlike_path, {
                    preserveScroll: true,
                  })
                "
              >
                <HeartIcon :filled="true" />
              </Button>
              <Button
                v-else
                variant="link"
                size="icon"
                class="cursor-pointer h-auto p-0"
                @click.prevent="
                  likeForm.post(recipe.like_path, { preserveScroll: true })
                "
              >
                <HeartIcon :filled="false" />
              </Button>
            </template>
            <Button
              v-else
              variant="link"
              size="icon"
              class="cursor-pointer h-auto p-0"
              @click.prevent="redirectToSignup"
            >
              <HeartIcon :filled="false" />
            </Button>
          </div>
        </div>
      </div>
    </Link>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { Link, useForm, router, usePage } from "@inertiajs/vue3";
import HeartIcon from "@/components/icons/HeartIcon.vue";
import { Button } from "@/components/ui/button";

const props = defineProps({
  recipe: { type: Object, required: true },
  currentUser: { type: Object, default: null },
});

const page = usePage();
const likeForm = useForm({});
const unlikeForm = useForm({});

const isLiked = computed(() => {
  if (!props.currentUser) return false;
  return props.currentUser.liked_recipe_ids.includes(props.recipe.id);
});

function redirectToSignup() {
  const newUserPath = page.props.navData?.newUserPath ?? "/users/new";
  router.visit(
    `${newUserPath}?return_to=/users/recipes&like_recipe_id=${props.recipe.id}`,
  );
}

// A small fixed palette — picks a consistent color per category name
const COLOR_PALETTE = [
  "#14b8a6", // teal
  "#f59e0b", // amber
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#3b82f6", // blue
  "#ef4444", // red
  "#10b981", // emerald
  "#f97316", // orange
];

function categoryColor(name) {
  if (props.recipe.category_color) return props.recipe.category_color;
  if (!name) return "#14b8a6";
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COLOR_PALETTE[Math.abs(hash) % COLOR_PALETTE.length];
}

function difficultyColor(level) {
  if (!level) return "#6c7a77";
  const l = level.toLowerCase();
  if (l === "enkel") return "#16a34a";
  if (l === "medel") return "#f59e0b";
  if (l === "avancerad") return "#ef4444";
  return "#6c7a77";
}
</script>
